/**
 * The single source of truth for what each plan costs and unlocks. Both the
 * public /pricing page and in-app Settings read from here, so the two can
 * never drift apart again.
 */
export type PlanId = "free" | "pro" | "team" | "enterprise";

export type Plan = {
  id: PlanId;
  name: string;
  priceMonthly: string;
  priceAnnual: string;
  unit: string;
  annualNote?: string;
  cta: string;
  action: "signup" | "contact";
  popular?: boolean;
  disabled?: boolean;
  priceNote?: string;
  featuresTitle: string;
  features: string[];
  /** One-line summary used in Settings. */
  blurb: string;
  /**
   * How many reach-outs the Today feed shows by default — the "focus" count.
   * A plain, non-locked "N more waiting" reveals up to maxDailyTouches.
   */
  defaultDailyTouches: number;
  /** Hard ceiling the expander may reveal up to; beyond this is locked/upsold. */
  maxDailyTouches: number;
  /**
   * How many people may be starred for Deep Watch. This is the real meter:
   * a starred person is re-read several times more often than anyone else,
   * so deep-watch slots — not head-count — are what a scan bill scales with.
   */
  maxDeepWatch: number;
  /** Manual Boosts allowed per calendar month. */
  monthlyBoosts: number;
  /** How many people you may watch on this plan. */
  maxPeople: number;
  /** Plain-English ongoing scan cadence for everyone outside Deep Watch. */
  scanCadence: string;
  /**
   * Live Context — the per-user Google Calendar + Gmail connection: prep before
   * meetings, hooks after them, real last-touch from the inbox, send as you.
   */
  liveContext: boolean;
  /** Active (upcoming or in-progress) "I'll be in town" trips allowed at once. */
  maxActiveTrips: number;
};

export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    priceMonthly: "Free",
    priceAnnual: "Free",
    unit: "forever",
    cta: "Get started",
    action: "signup",
    blurb: "5 people · 1 reason to reach out a day.",
    defaultDailyTouches: 1,
    maxDailyTouches: 1,
    maxDeepWatch: 1,
    monthlyBoosts: 3,
    maxPeople: 5,
    liveContext: false,
    maxActiveTrips: 1,
    scanCadence: "Re-scanned every few days",
    featuresTitle: "What you get:",
    features: [
      "5 people you're watching",
      "Full first scan the moment you add someone — role, company, links, first signal",
      "Basic Signal Engine™: public web signals, your memories, your drafts",
      "1 reason to reach out each day, with the message already written",
      "Today feed with stoplight urgency",
      "1 person on Deep Watch — read daily, like a paid plan",
      "Everyone else re-scanned every few days",
      "3 manual Boosts a month for an instant re-read",
      "Unlimited voice memos and memories",
      "Full signal history — nothing expires",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    priceMonthly: "$29",
    priceAnnual: "$24",
    unit: "/ month",
    annualNote: "$288 billed yearly",
    cta: "Start free trial",
    action: "signup",
    popular: true,
    blurb: "250 people, scanned daily · up to 10 reasons a day (5 by default).",
    defaultDailyTouches: 5,
    maxDailyTouches: 10,
    maxDeepWatch: 25,
    monthlyBoosts: 50,
    maxPeople: 250,
    liveContext: true,
    maxActiveTrips: 25,
    scanCadence: "Everyone scanned daily",
    featuresTitle: "Everything in Free, plus:",
    features: [
      "Pro Signal Engine™: 250 people, every one of them scanned daily",
      "Live Context — calendar prep before meetings, hooks after them, real last-touch from your inbox",
      "Send follow-ups from your own address",
      "Up to 10 reasons a day (5 in focus by default)",
      "Deep Watch on 25 people — read several times a day",
      "50 manual Boosts a month",
      "Interest and topic monitoring beyond your people",
      "Travel mode — \"I'll be in town\" drafts",
      "Export everything, anytime",
    ],
  },
  {
    id: "team",
    name: "Team & Enterprise",
    priceMonthly: "Let's chat",
    priceAnnual: "Let's chat",
    unit: "",
    priceNote: "from $59 per seat",
    cta: "Talk to us",
    action: "contact",
    blurb: "Shared circles, seats and SSO · up to 20 reasons a day.",
    defaultDailyTouches: 5,
    maxDailyTouches: 20,
    maxDeepWatch: 200,
    monthlyBoosts: 1000,
    maxPeople: 100000,
    liveContext: true,
    maxActiveTrips: 100,
    scanCadence: "Everyone scanned daily",
    featuresTitle: "Everything in Pro, plus:",
    features: [
      "Pro Signal Engine™ on unlimited people and seats, all scanned daily",
      "Up to 20 reasons a day per seat",
      "Deep Watch on 50+ people per seat",
      "Shared circles and groups across the team",
      "See who already knows a person",
      "Shared memories and notes",
      "Team-wide coverage warnings",
      "Admin tools and controls",
      "SSO and SAML, custom data retention",
      "Security review, DPA and invoice billing",
      "Dedicated success manager and custom integrations",
    ],
  },
];

export const DEFAULT_PLAN: PlanId = "free";

export function planOf(value: unknown): Plan {
  // "enterprise" is a legacy stored value — it now maps to the merged Team plan.
  const id = value === "enterprise" ? "team" : value;
  return PLANS.find((p) => p.id === id) ?? PLANS[0]!;
}

/** Legacy sentinel that used to mean "no cap"; now folded down to the plan max. */
export const UNLIMITED_DAILY_TOUCHES = 999;

/** Server and client both clamp through here, so a Free user can't ask for 10. */
export function clampDailyTouches(value: unknown, plan: unknown): number {
  const p = planOf(plan);
  const max = p.maxDailyTouches;
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return p.defaultDailyTouches;
  return Math.min(max, Math.max(1, n));
}

/** Deep-watch slots on a plan — the cap the star toggle enforces. */
export function maxDeepWatchFor(plan: unknown): number {
  return planOf(plan).maxDeepWatch;
}

/** Manual Boosts a plan allows per calendar month. */
export function monthlyBoostsFor(plan: unknown): number {
  return planOf(plan).monthlyBoosts;
}

/** People a plan may watch. */
export function maxPeopleFor(plan: unknown): number {
  return planOf(plan).maxPeople;
}

/** Whether a plan may turn on Live Context (Google Calendar + Gmail). */
export function canUseLiveContext(plan: unknown): boolean {
  return planOf(plan).liveContext;
}

/** Active trips a plan may keep at once — Travel mode is a Pro headline feature. */
export function maxActiveTripsFor(plan: unknown): number {
  return planOf(plan).maxActiveTrips;
}

/**
 * Interest and topic monitoring — standing searches beyond your people. Pro
 * and Team only, exactly as /pricing says.
 */
export function canUseTopics(plan: unknown): boolean {
  return planOf(plan).liveContext;
}

/** The next tier up, for "upgrade to X" copy. Team is the top of the ladder. */
export function nextPlanUp(plan: unknown): Plan | null {
  const id = planOf(plan).id;
  if (id === "free") return planOf("pro");
  if (id === "pro") return planOf("team");
  return null;
}

/**
 * The side-by-side table on /pricing. Kept here so a cap can never be changed
 * in one place and quoted differently in another.
 */
export type ComparisonRow = { label: string; free: string; pro: string; team: string };

export const COMPARISON: ComparisonRow[] = [
  { label: "People you're watching", free: "5", pro: "250", team: "Unlimited" },
  { label: "Signal Engine™", free: "Basic", pro: "Pro", team: "Pro" },
  { label: "Full scan when you add someone", free: "Yes", pro: "Yes", team: "Yes" },
  { label: "Ongoing scanning", free: "Every few days", pro: "Daily", team: "Daily" },
  { label: "Deep Watch — read daily, several times", free: "1 person", pro: "25 people", team: "50+ per seat" },
  { label: "Reasons to reach out each day", free: "1", pro: "Up to 10", team: "Up to 20" },
  { label: "Drafted messages in your voice", free: "Yes", pro: "Yes", team: "Yes" },
  { label: "Memories and voice memos", free: "Unlimited", pro: "Unlimited", team: "Unlimited" },
  { label: "Manual Boosts a month", free: "3", pro: "50", team: "Unlimited" },
  { label: "Live Context — calendar + inbox", free: "—", pro: "Yes", team: "Yes" },
  { label: "Send from your own address", free: "—", pro: "Yes", team: "Yes" },
  { label: "Interest and topic monitoring", free: "—", pro: "Yes", team: "Yes" },
  { label: "Signal history", free: "Full", pro: "Full", team: "Full + custom retention" },
  { label: "Shared circles, SSO, admin controls", free: "—", pro: "—", team: "Yes" },
];
